/**
 * Empty state for a feature the org has never used: what it is, how it works, and a
 * prompt to paste into a coding agent that sets it up. Composes the shared `EmptyState`.
 *
 * @module ui/components/FeatureOnboardingEmptyState
 */
import React from 'react';
import { Button, EmptyState, Text, useToast } from '@chatsdet/ui';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Check, CopySimple } from '@phosphor-icons/react';
import { copyTextToClipboard } from './settings/clipboard-copy';

/** What the "Get started" section offers: a prompt written here, or a Prompt Library entry that owns its text. */
type OnboardingPrompt =
  | {
      /** Plain-text prompt the user pastes into their coding agent. */
      agentPrompt: string;
      promptLibrarySlug?: never;
    }
  | {
      /** Slug of the Prompt Library entry to open; its text is not duplicated here. */
      promptLibrarySlug: string;
      agentPrompt?: never;
    };

type FeatureOnboardingEmptyStateProps = OnboardingPrompt & {
  icon: React.ReactNode;
  /** Animated brand graphic that shows what the feature does. */
  graphic: React.ReactNode;
  title: string;
  /** One or two plain sentences on what the feature is for. */
  whatItIs: string;
  /** Three or four short steps. */
  steps: string[];
};

function CopyPromptButton({ prompt }: { prompt: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    if (await copyTextToClipboard(prompt)) {
      setCopied(true);
      toast.success('Copied prompt');
      window.setTimeout(() => setCopied(false), 1200);
    } else {
      toast.error('Failed to copy');
    }
  };

  return (
    <Button variant="outline" size="sm" className="gap-1.5" onPress={copy} aria-label="Copy prompt">
      {copied ? <Check size={14} /> : <CopySimple size={14} />}
      {copied ? 'Copied' : 'Copy prompt'}
    </Button>
  );
}

export function FeatureOnboardingEmptyState(props: FeatureOnboardingEmptyStateProps) {
  const { icon, graphic, title, whatItIs, steps } = props;
  return (
    <EmptyState
      icon={icon}
      graphic={graphic}
      title={title}
      description={whatItIs}
      data-testid="feature-onboarding"
      // `action` is the slot that renders below the description; it carries the whole onboarding body here.
      action={
      <div className="mx-auto w-full max-w-2xl space-y-6 pt-2 text-left">
        <section className="space-y-2">
          <Text size="sm" className="font-semibold">
            How it works
          </Text>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
        {props.agentPrompt !== undefined ? (
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Text size="sm" className="font-semibold">
                Get started
              </Text>
              <CopyPromptButton prompt={props.agentPrompt} />
            </div>
            <Text size="sm" className="text-muted-foreground">
              Paste this into your coding agent (Claude Code, Codex, or similar).
            </Text>
            <pre
              data-testid="onboarding-prompt"
              className="whitespace-pre-wrap break-words rounded-md border border-border bg-muted p-4 text-sm font-mono text-foreground"
            >
              {props.agentPrompt}
            </pre>
          </section>
        ) : (
          <section className="space-y-2">
            <Text size="sm" className="font-semibold">
              Get started
            </Text>
            <Text size="sm" className="text-muted-foreground">
              Copy the setup prompt from the Prompt Library and paste it into your coding agent (Claude Code, Codex, or
              similar).
            </Text>
            <Link
              to="/prompt-library/$promptId"
              params={{ promptId: props.promptLibrarySlug }}
              data-testid="onboarding-prompt-link"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Open the setup prompt
              <ArrowRight size={14} />
            </Link>
          </section>
        )}
      </div>
      }
    />
  );
}
